import type { Feature, FeatureCollection, Polygon, Position } from 'geojson';
import { formatNumber } from 'utils/number';
import type { Circle, PickedSurveyArea } from './types';

/** Stable empty set of picked survey areas, so a cleared pick doesn't re-notify. */
export const EMPTY_PICKED_AREAS: PickedSurveyArea[] = [];

/**
 * Everything under construction — drawn polygons and the derived preview — is
 * blue. An area's Display Color describes the saved area, not the thing being
 * built (UAT 2).
 */
export const PREVIEW_COLOR = '#0F62FE';

/**
 * Below this the survey-area tile is empty by design — the grid is too dense to
 * rasterize — so there is nothing to pick and the map says so instead of looking
 * broken. Mirrors `Map::Tiles::ReferenceLocations::MIN_ZOOM`.
 */
export const SURVEY_PICK_MIN_ZOOM = 10;

/** Feet per degree of latitude, the mock's flat-earth constant for circles. */
export const FT_PER_DEGREE_LAT = 364_320;

/** A circle's ring has this many sides, closed by repeating the first vertex. */
export const CIRCLE_SEGMENTS = 64;

/** Shorter than this, a second click is taken as a slip rather than a circle. */
export const MIN_CIRCLE_RADIUS_FT = 10;

/** The ring vertex the radius label and the resize handle sit on: due north. */
export const CIRCLE_HANDLE_INDEX = CIRCLE_SEGMENTS / 4;

/**
 * Feet per degree of longitude at a latitude.
 *
 * @param {number} lat - Latitude in degrees.
 * @return {number} Feet per degree of longitude there.
 */
const ftPerDegreeLng = (lat: number): number =>
  FT_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);

/**
 * Ground distance in feet between two positions, flat-earth, as the mock measures a
 * radius. Plenty for the few miles an area's circle spans.
 *
 * @param {Position} from - The centre.
 * @param {Position} to - The pointer.
 * @return {number} The distance in feet.
 */
export const distanceFt = (from: Position, to: Position): number =>
  Math.hypot(
    (to[0] - from[0]) * ftPerDegreeLng(from[1]),
    (to[1] - from[1]) * FT_PER_DEGREE_LAT
  );

/**
 * A circle as the polygon the backend stores: a closed ring of
 * {@link CIRCLE_SEGMENTS} sides, starting due east and turning anticlockwise.
 *
 * @param {Position} center - The centre, [lng, lat].
 * @param {number} radiusFt - The radius in feet.
 * @return {Polygon} The circle's polygon.
 */
export const circlePolygon = (center: Position, radiusFt: number): Polygon => {
  const [lng, lat] = center;
  const ring: Position[] = Array.from({ length: CIRCLE_SEGMENTS }, (_, i) => {
    const angle = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    return [
      lng + (Math.cos(angle) * radiusFt) / ftPerDegreeLng(lat),
      lat + (Math.sin(angle) * radiusFt) / FT_PER_DEGREE_LAT,
    ];
  });
  return { type: 'Polygon', coordinates: [[...ring, ring[0]]] };
};

/**
 * Reads a circle back out of a polygon drawn by {@link circlePolygon}, so a circle
 * needs no state of its own: its centre is midway between the east and west
 * vertices, and every vertex sits a radius from it. Anything else is a polygon.
 *
 * @param {Polygon} polygon - A drawn shape.
 * @return {{ center: Position, radiusFt: number } | null} The circle, or null.
 */
export const circleFromPolygon = (
  polygon: Polygon
): { center: Position; radiusFt: number } | null => {
  const ring = polygon.coordinates[0];
  if (polygon.coordinates.length !== 1 || ring.length !== CIRCLE_SEGMENTS + 1) {
    return null;
  }
  const east = ring[0];
  const west = ring[CIRCLE_SEGMENTS / 2];
  const center: Position = [(east[0] + west[0]) / 2, (east[1] + west[1]) / 2];
  const radiusFt = distanceFt(center, east);
  if (radiusFt < 1) return null;
  const tolerance = Math.max(0.5, radiusFt * 1e-4);
  const round = ring.every(
    (vertex) => Math.abs(distanceFt(center, vertex) - radiusFt) <= tolerance
  );
  return round ? { center, radiusFt } : null;
};

/**
 * The label beside a circle being sized, as the mock words it.
 *
 * @param {number} radiusFt - The radius in feet.
 * @return {string} "Radius: 1,320 ft".
 */
export const radiusLabel = (radiusFt: number): string =>
  `Radius: ${formatNumber(Math.round(radiusFt), 0, 0)} ft`;

/**
 * Wraps a geometry as a feature for a map source.
 *
 * @param {Feature['geometry']} geometry - The geometry.
 * @param {object} properties - Its properties.
 * @return {Feature} The feature.
 */
const toFeature = (
  geometry: Feature['geometry'],
  properties: Record<string, unknown> = {}
): Feature => ({ type: 'Feature', properties, geometry });

/**
 * The circle being drawn or dragged, as the mock paints it: its centre, and once it
 * has a radius, its ring and the north point the label sits on.
 *
 * @param {Circle | null} circle - The circle in progress, if any.
 * @param {number} handleIndex - The ring vertex the label point sits on.
 * @return {FeatureCollection} The draft features.
 */
export const circleDraftFeatures = (
  circle: Circle | null,
  handleIndex: number = CIRCLE_HANDLE_INDEX
): FeatureCollection => {
  if (!circle) return { type: 'FeatureCollection', features: [] };
  const features = [toFeature({ type: 'Point', coordinates: circle.center })];
  if (circle.radiusFt > 0) {
    const ring = circlePolygon(circle.center, circle.radiusFt);
    features.push(
      toFeature(ring),
      toFeature({
        type: 'Point',
        coordinates: ring.coordinates[0][handleIndex],
      })
    );
  }
  return { type: 'FeatureCollection', features };
};

/**
 * A centre handle (move) and a north handle (resize) for each drawn circle.
 *
 * @param {Array} circles - Each circle with its index in the drawn set.
 * @param {number} handleIndex - The ring vertex the resize handle sits on.
 * @return {FeatureCollection} The handle points.
 */
export const circleHandleFeatures = (
  circles: { index: number; circle: Circle }[],
  handleIndex: number = CIRCLE_HANDLE_INDEX
): FeatureCollection => ({
  type: 'FeatureCollection',
  features: circles.flatMap(({ index, circle }) => [
    toFeature(
      { type: 'Point', coordinates: circle.center },
      { index, kind: 'move' }
    ),
    toFeature(
      {
        type: 'Point',
        coordinates: circlePolygon(circle.center, circle.radiusFt)
          .coordinates[0][handleIndex],
      },
      { index, kind: 'resize' }
    ),
  ]),
});

/**
 * A circle being dragged, with the pointer where it is: moved by its centre, or
 * resized by its handle.
 *
 * @param {Circle} circle - The circle as the drag found it.
 * @param {'move' | 'resize'} kind - Which handle is held.
 * @param {Position} pointer - Where the pointer is.
 * @return {Circle} The circle under the pointer.
 */
export const draggedCircle = (
  circle: Circle,
  kind: 'move' | 'resize',
  pointer: Position
): Circle =>
  kind === 'move'
    ? { ...circle, center: pointer }
    : { ...circle, radiusFt: distanceFt(circle.center, pointer) };

/** What a drag reports: where the pointer is, as it moves and when it lets go. */
export interface DragHandlers {
  move: (position: Position) => void;
  end: (position: Position) => void;
}

/**
 * Follows a drag on the window rather than the map. Mapbox only hears the
 * pointer while it is over the canvas, so a release over a tooltip, the rail or
 * outside the modal never reached it and left the drag running. Positions are
 * the map's, read from the pointer's screen position.
 *
 * @param {object} map - The Mapbox map the drag started on.
 * @param {DragHandlers} handlers - What to do as it moves and when it ends.
 * @return {Function} Stops following, for a drag cut short (Esc, unmount).
 */
export const followDrag = (
  map: {
    getCanvasContainer: () => HTMLElement;
    unproject: (point: [number, number]) => { lng: number; lat: number };
  },
  handlers: DragHandlers
): (() => void) => {
  /**
   * The map position under a pointer event.
   *
   * @param {MouseEvent} event - The pointer event.
   * @return {Position} [lng, lat].
   */
  const at = (event: MouseEvent): Position => {
    const rect = map.getCanvasContainer().getBoundingClientRect();
    const { lng, lat } = map.unproject([
      event.clientX - rect.left,
      event.clientY - rect.top,
    ]);
    return [lng, lat];
  };
  /**
   * Reports a move.
   *
   * @param {MouseEvent} event - The move.
   */
  const onMove = (event: MouseEvent) => handlers.move(at(event));
  /** Stops listening. */
  const stop = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };
  /**
   * Reports the release and stops.
   *
   * @param {MouseEvent} event - The release.
   */
  const onUp = (event: MouseEvent) => {
    stop();
    handlers.end(at(event));
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
  return stop;
};
